import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import VideoPlayer from "@/components/VideoPlayer";
import { Activity, TrendingUp } from "lucide-react";
import { LogoIcon } from "@/components/Wordmark";

// Public share page — no auth. Anyone with the token can view.
// Small TradeGreen watermark overlay + branded page around it for viral growth.
export default function SharedClip() {
  const { token } = useParams();
  const [clip, setClip] = useState(null);
  const [error, setError] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    axios.get(`${backendUrl}/api/video/share/${token}`)
      .then((r) => setClip(r.data))
      .catch(() => setError("This clip is unavailable or has been unshared."));
  }, [token, backendUrl]);

  const streamUrl = `${backendUrl}/api/video/share/${token}/stream`;

  const fmtUSD = (n) => {
    const v = Number(n) || 0;
    return `${v >= 0 ? "+" : "-"}$${Math.abs(v).toFixed(2)}`;
  };

  return (
    <div className="min-h-screen relative" style={{ background: "var(--bg)", color: "var(--fg)", fontFamily: "var(--font-body)" }}>
      {/* Aurora */}
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 900px 500px at 8% 5%, rgba(34,197,94,0.06), transparent 60%),
            radial-gradient(ellipse 900px 500px at 92% 105%, rgba(139,92,246,0.10), transparent 60%)
          `,
        }}
      />

      {/* Header */}
      <header className="relative px-8 py-5 flex items-center justify-between" style={{ borderBottom: "1px solid var(--border)" }}>
        <a href="/" className="flex items-center gap-2">
          <LogoIcon size={22} />
          <span className="font-heading text-lg font-semibold tracking-tight">TRADE<span style={{ color: "var(--green)" }}>GREEN</span></span>
        </a>
        <a
          href="/"
          data-testid="share-cta"
          className="px-4 py-2 text-xs font-semibold tracking-wide"
          style={{ background: "var(--accent)", color: "white", borderRadius: "8px" }}
        >
          TRY TRADEGREEN
        </a>
      </header>

      <div className="relative max-w-4xl mx-auto px-6 py-10">
        {error && (
          <div className="p-8 text-center" style={{ color: "var(--fg-2)" }}>{error}</div>
        )}
        {clip && (
          <>
            <div className="mb-6">
              <div className="label-eyebrow" style={{ color: "var(--accent)" }}>SHARED TRADE CLIP</div>
              <h1 className="font-heading text-3xl md:text-4xl font-semibold mt-2 tracking-tight flex items-center gap-3">
                <span>{clip.symbol}</span>
                <span
                  className="text-lg font-mono-num"
                  style={{ color: clip.pnl >= 0 ? "var(--green)" : "var(--loss)" }}
                  data-testid="shared-pnl"
                >
                  {fmtUSD(clip.pnl)}
                </span>
                {clip.side && (
                  <span className="text-xs font-mono-num px-2 py-1" style={{ background: "var(--accent-soft)", color: "var(--accent)", borderRadius: "6px" }}>
                    {clip.side.toUpperCase()}
                  </span>
                )}
              </h1>
              <div className="text-sm mt-1" style={{ color: "var(--fg-3)" }}>
                {clip.date} · {new Date(clip.entry_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>

            <VideoPlayer clip={clip} streamUrl={streamUrl} readOnly showWatermark />

            <div
              className="mt-8 p-6 flex items-center gap-4"
              style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "14px" }}
            >
              <TrendingUp className="h-6 w-6" strokeWidth={1.5} style={{ color: "var(--accent)" }} />
              <div className="flex-1">
                <div className="font-heading text-sm font-semibold">Journal your trades like game film</div>
                <div className="text-xs mt-0.5" style={{ color: "var(--fg-3)" }}>TradeGreen auto-cuts screen recordings into a clip per trade. Nobody else does this.</div>
              </div>
              <a
                href="/"
                className="px-4 py-2 text-xs font-semibold tracking-wide"
                style={{ background: "var(--accent)", color: "white", borderRadius: "8px" }}
              >
                LEARN MORE →
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
